import { useEffect, useRef, useState } from 'react';

export const DEFAULT_PAGE_SIZE = 10;

/**
 * Progressive client-side reveal ("endless scroll") over an already-loaded list.
 *
 * Shows `pageSize` items at first and reveals `pageSize` more each time the
 * element attached to `sentinelRef` scrolls into view.
 *
 * The revealed count is kept when `items` is merely refreshed (e.g. a refetch
 * after a create/edit/delete) so the list and scroll position don't collapse
 * (#24). It resets to `pageSize` only when `resetKey` changes — pass a value
 * that changes whenever the *selection* of items changes (e.g. the active
 * filters) so a new filter starts from the top again.
 *
 * @param {Array} items                     The full list to reveal progressively.
 * @param {object} [options]
 * @param {number} [options.pageSize=10]    How many items to show initially and per reveal.
 * @param {*}      [options.resetKey]       Any primitive; the reveal restarts when it changes.
 * @returns {{ visibleItems: Array, visibleCount: number, hasMore: boolean, sentinelRef: import('react').RefObject }}
 *   `hasMore` is derived from `visibleCount < items.length`; render the sentinel
 *   only while it is true.
 */
export const useInfiniteReveal = (items = [], { pageSize = DEFAULT_PAGE_SIZE, resetKey } = {}) => {
  const [visibleCount, setVisibleCount] = useState(pageSize);
  const sentinelRef = useRef(null);

  const total = items.length;
  const hasMore = visibleCount < total;

  // Restart the reveal only when the caller's reset key changes (a filter change),
  // not when `items` is refreshed with new data
  useEffect(() => {
    setVisibleCount(pageSize);
  }, [resetKey, pageSize]);

  // Observe the sentinel to reveal more. Re-observe after every reveal (visibleCount is a
  // dependency): a newly observed target always gets an initial callback, so if the sentinel
  // is still on screen after a reveal — tall viewports, or a reveal that leaves it straddling
  // the bottom edge, where no threshold is crossed — the next page loads too, until the
  // sentinel is actually out of view or the list is exhausted.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore) return;

    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCount((prev) => Math.min(prev + pageSize, total));
        }
      },
      // threshold 0: fire as soon as any part of the sentinel is visible. A threshold of
      // 1.0 requires intersectionRatio to reach exactly 1, which fractional-pixel layout
      // under browser zoom / non-integer devicePixelRatio can never satisfy (#25).
      { root: null, rootMargin: '0px', threshold: 0 }
    );

    io.observe(sentinel);
    return () => io.disconnect();
  }, [hasMore, total, pageSize, visibleCount]);

  return {
    visibleItems: items.slice(0, visibleCount),
    visibleCount,
    hasMore,
    sentinelRef,
  };
};
